import CONFIG from 'Config/settings';
import { ReduxStore } from 'Redux';
import { getFormValues } from 'Redux/forms/formsHelper';
import { t } from 'Utils/localization/i18next';
import validationMessage from '../validationMessage/validationMessage';
import {
  getPrimitiveRegExp,
  matchesFieldPolicy,
  resolveFieldPolicyRegExps
} from '../validationRules/validationRules';

const skipEmpty = validator => (value = '', ...args) => {
  if (!value) {
    return undefined;
  }

  return validator(value, ...args);
};

const createPrimitiveValidator = (primitiveId, messageKey, forceIgnoreCase) =>
  skipEmpty((value, label, { message } = {}) => {
    const regexp = getPrimitiveRegExp(primitiveId, forceIgnoreCase);

    if (!regexp || !regexp.test(value)) {
      return validationMessage(messageKey, label, message);
    }

    return undefined;
  });

const addressLineWithApartmentNumber = createPrimitiveValidator(
  'address_with_apartment',
  'addressLineWithApartmentNumber',
  true
);

const notInjectionChars = createPrimitiveValidator(
  'not_injection_chars',
  'notInjectionChars'
);

const notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars = createPrimitiveValidator(
  'address_no_numbers',
  'notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars'
);

const notOnlySpaceNumberSpecialCharsAndNotInjectionChars = createPrimitiveValidator(
  'address_basic',
  'notOnlySpaceNumberSpecialCharsAndNotInjectionChars'
);

const notOnlySpaceSpecialCharsAndNotInjectionChars = createPrimitiveValidator(
  'safe_text_content',
  'notOnlySpaceSpecialCharsAndNotInjectionChars'
);

const zipCode = (value = '', formId) => {
  if (!value.length) {
    return undefined;
  }

  const formValues = getFormValues(ReduxStore.getState(), formId);
  const country = formValues.addressCountryCode || CONFIG.COUNTRY.toUpperCase();
  const regexp = getPrimitiveRegExp(`${country.toLowerCase()}.postal_code`);

  if (!regexp || !regexp.test(value)) {
    return [
      country === CONFIG.COUNTRY.toUpperCase()
        ? t('forms.validation.zipCode')
        : t('forms.validation.zipCodeInternational', { country })
    ];
  }

  return undefined;
};

const phone = (value = '', { phoneType: configuredPhoneType } = {}, formId) => {
  if (!value.length) {
    return undefined;
  }
  const phoneType = configuredPhoneType === 'landline' ? 'landline' : 'mobile';
  const formValues = getFormValues(ReduxStore.getState(), formId);
  const country =
    formValues[`${phoneType}Country`] || CONFIG.COUNTRY.toUpperCase();
  const fieldKey =
    phoneType === 'landline' ? 'LANDLINE_NUMBER' : 'MOBILE_NUMBER';
  const resolvedPolicy = resolveFieldPolicyRegExps(fieldKey, country);

  if (resolvedPolicy && !matchesFieldPolicy(value, resolvedPolicy)) {
    return [
      country === CONFIG.COUNTRY.toUpperCase()
        ? t(`forms.validation.${phoneType}`)
        : t(`forms.validation.${phoneType}International`)
    ];
  }

  if (resolvedPolicy) {
    return undefined;
  }

  const phoneField =
    phoneType === 'landline' ? 'landline_number' : 'mobile_number';
  const phonePrimitiveId = `${country.toLowerCase()}.${phoneField}`;
  const regexp = getPrimitiveRegExp(phonePrimitiveId);

  if (!regexp || !regexp.test(value)) {
    return [
      country === CONFIG.COUNTRY.toUpperCase()
        ? t(`forms.validation.${phoneType}`)
        : t(`forms.validation.${phoneType}International`)
    ];
  }
  return undefined;
};

export {
  addressLineWithApartmentNumber,
  notInjectionChars,
  notNumbersAndnotOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceNumberSpecialCharsAndNotInjectionChars,
  notOnlySpaceSpecialCharsAndNotInjectionChars,
  phone,
  zipCode
};
